import {
  requestGetModelMailForOffice,
  requestGetModelMailFromId,
} from "@/state/modelMail/ModelMailEffects";
import { useCurrentTransaction } from "@/state/transactions/CurrentTransactionHook";
import { UserMailConfiguration } from "@/type/core/auth/auth.types";
import {
  CreateModelMail,
  ModelMail,
  ModelMailTypes,
  OfficeModels,
} from "@/type/core/models/models.types";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import { MailHelper } from "@/utils/helpers/mail-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import useOfficesToDisplay from "@/utils/hooks/useOfficesToDisplay";
import BasicButton from "@components/core/buttons/BasicButton";
import SubmitButton from "@components/core/buttons/SubmitButton";
import CheckboxFormField from "@components/core/inputs/CheckboxFormField";
import InputFormField from "@components/core/inputs/InputFormField";
import RemirrorField from "@components/core/inputs/Remirror/RemirrorField";
import TextAreaFormField from "@components/core/inputs/TextAreaFormField";
import ChangeModelMailModal from "@components/core/modals/ChangeModelMailModal";
import PreviewMailModal from "@components/core/modals/PreviewMailModal";
import ChevronIcon from "@static/core/icons/icon-chevron.svg?react";
import EditIcon from "@static/core/icons/icon-edit.svg?react";
import ViewIcon from "@static/core/icons/icon-view.svg?react";
import { MAIL_DEFAULT_SUBJECT } from "@utils/Constant";
import { Col, Form, Radio, Row } from "antd";
import { FormInstance } from "antd/lib";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface SignatureMailFormProps {
  mailType: ModelMailTypes;
  officeId?: string;
  isFromModel?: boolean;
  isFromScellement?: boolean;
  mailContentVisible?: boolean;
  className?: string;
  form?: FormInstance<CreateModelMail>;
  transactionType?: TransactionType;
  invitationMailOfficeOrUserName?: string;
  invitationMailName?: string;
  endOfTransactionMailOfficeOrUserName?: string;
  endOfTransactionMailName?: string;
  sealEndOfTransactionMailOfficeOrUserName?: string;
  sealEndOfTransactionMailName?: string;
  signatureInitialValue: string;
  setSignatureInitialValue: (newSignatureInitialValue: string) => void;
  triggerFormChanged?: () => void;
}

const SignatureMailForm: FunctionComponent<SignatureMailFormProps> = ({
  mailType,
  officeId,
  isFromModel = false,
  isFromScellement = false,
  mailContentVisible = false,
  className = "",
  form,
  transactionType,
  invitationMailOfficeOrUserName,
  invitationMailName,
  endOfTransactionMailOfficeOrUserName,
  endOfTransactionMailName,
  sealEndOfTransactionMailOfficeOrUserName,
  sealEndOfTransactionMailName,
  signatureInitialValue,
  setSignatureInitialValue,
  triggerFormChanged,
}) => {
  const { t } = useTranslation();

  const [isMailContentVisible, setIsMailContentVisible] =
    useState(mailContentVisible);
  const [isPreviewMailModalOpen, setIsPreviewMailModalOpen] =
    useState<boolean>(false);
  const [isChangeModelMailModalOpen, setIsChangeModelMailModalOpen] =
    useState<boolean>(false);
  const [modelsList, setModelsList] = useState<OfficeModels[]>([]);
  const [officeOrUserNameOverrided, setOfficeOrUserNameOverrided] =
    useState<string>();
  const [nameOverrided, setNameOverrided] = useState<string>();

  const { officesToDisplay } = useOfficesToDisplay();
  const { transactionForm } = useCurrentTransaction();

  useEffect(() => {
    if (!isFromModel && officeId) {
      requestGetModelMailForOffice({
        dto: { type: mailType },
        id: officeId,
      })
        .then((response) => {
          if (response.ok && response.data) {
            setModelsList(response.data);
          } else {
            toastError(t("error.toast.modelMail.retrieve"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.modelMail.retrieve"));
        });
    }
  }, [mailType, officeId, isFromModel]);

  const mailConfiguration: UserMailConfiguration = {
    message: form?.getFieldValue("message"),
    senderEmailLabel: form?.getFieldValue("senderEmailLabel"),
    senderEmailLabelText: form?.getFieldValue("senderEmailLabelText"),
    signature: form?.getFieldValue("signature"),
    subject: form?.getFieldValue("subject"),
  };

  const setFormValues = (data: ModelMail) => {
    const formModelMail: CreateModelMail = {
      defaultModel: data.defaultModel,
      message: data.mailConfiguration?.message ?? "",
      senderEmailLabel: data.mailConfiguration?.senderEmailLabel ?? "",
      senderEmailLabelText: data.mailConfiguration?.senderEmailLabelText ?? "",
      signature: data.mailConfiguration?.signature ?? "",
      subject: data.mailConfiguration?.subject ?? "",
      name: data.name,
    };
    form?.setFieldsValue(formModelMail);
    setSignatureInitialValue(formModelMail.signature);
  };

  const handleChangeModelMail = (id?: string) => {
    if (!id) {
      toastError(t("error.toast.modelMail.mail"));
      return;
    }
    requestGetModelMailFromId({ id })
      .then((response) => {
        if (response.ok && response.data) {
          setFormValues(response.data);
          setNameOverrided(response.data.name);
          setOfficeOrUserNameOverrided(response.data.officeOrUserName);
          triggerFormChanged?.();
        } else {
          toastError(t("error.toast.modelMail.mail"));
        }
      })
      .catch(() => {
        toastError(t("error.toast.modelMail.mail"));
      });
  };

  const renderModelUsed = (officeOrUserName: string, name: string) => {
    return (
      <div className="d-flex mt-0 mb-48 font-italic">
        {t("offices.mailModel.usedModel")} {officeOrUserName + " - " + name}
      </div>
    );
  };

  const correctSender = (): string => {
    return `${MailHelper.getSenderLabelFromMailConfiguration(
      mailConfiguration,
      officesToDisplay.filter(
        (office) => office.id === transactionForm.office,
      )[0],
    )} <noreply@jesignexpert.com>`;
  };

  const correctSubject = (): string => {
    if (mailConfiguration.subject && mailConfiguration.subject !== "") {
      return mailConfiguration.subject;
    } else if (mailType === "endOfTransaction") {
      if (transactionType) {
        return MAIL_DEFAULT_SUBJECT.END_OF_TRANSACTION + transactionType;
      } else {
        return MAIL_DEFAULT_SUBJECT.END_OF_TRANSACTION;
      }
    } else {
      return MAIL_DEFAULT_SUBJECT.INVITATION;
    }
  };

  return (
    <>
      {!isFromModel && (
        <div
          className={`d-flex align-items-center flex-wrap gap-16 mt-48 mb-0 ${className}`}
          data-test="section"
          data-alt={t(`offices.mailModel.form.label.${mailType}`)}
        >
          <span className="pb-4 text-dark">
            {t(`offices.mailModel.form.label.${mailType}`)}
          </span>
          <div className="d-flex flex-grow-1 justify-content-end flex-wrap">
            <BasicButton
              variant="transparent"
              icon={<ViewIcon />}
              text={t(`offices.mailModel.form.${mailType}.preview.label_short`)}
              onClick={() => setIsPreviewMailModalOpen(true)}
            />
            <BasicButton
              variant="transparent"
              text={t("offices.mailModel.form.changeModel.label")}
              onClick={() => setIsChangeModelMailModalOpen(true)}
            />
            <BasicButton
              variant="transparent"
              icon={<EditIcon />}
              text={t("offices.mailModel.form.edit.link")}
              onClick={() => setIsMailContentVisible(!isMailContentVisible)}
            />
            <div
              className="d-flex align-items-center cursor-pointer"
              onClick={() => setIsMailContentVisible(!isMailContentVisible)}
            >
              <ChevronIcon
                className={`icon-30 ${
                  isMailContentVisible ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        </div>
      )}
      {!isFromModel &&
        nameOverrided &&
        officeOrUserNameOverrided &&
        renderModelUsed(officeOrUserNameOverrided, nameOverrided)}
      {!isFromModel &&
        !nameOverrided &&
        !officeOrUserNameOverrided &&
        mailType &&
        mailType === "invitation" &&
        invitationMailOfficeOrUserName &&
        invitationMailName &&
        renderModelUsed(invitationMailOfficeOrUserName, invitationMailName)}
      {!isFromModel &&
        !nameOverrided &&
        !officeOrUserNameOverrided &&
        mailType &&
        mailType === "endOfTransaction" &&
        !isFromScellement &&
        endOfTransactionMailOfficeOrUserName &&
        endOfTransactionMailName &&
        renderModelUsed(
          endOfTransactionMailOfficeOrUserName,
          endOfTransactionMailName,
        )}
      {!isFromModel &&
        !nameOverrided &&
        !officeOrUserNameOverrided &&
        mailType &&
        mailType === "endOfTransaction" &&
        isFromScellement &&
        sealEndOfTransactionMailOfficeOrUserName &&
        sealEndOfTransactionMailName &&
        renderModelUsed(
          sealEndOfTransactionMailOfficeOrUserName,
          sealEndOfTransactionMailName,
        )}
      <div className={isMailContentVisible ? "" : "d-none"}>
        {isFromModel && (
          <InputFormField
            showLabel
            showTooltip
            labelTooltip="modelNameMail"
            module="offices.mailModel.form"
            field="name"
            maxLength={45}
            className="mt-16"
            required
          />
        )}
        <label className="d-block text-dark mb-8">
          {t("offices.mailModel.form.senderEmailLabel.label")}
        </label>
        <Form.Item name={"senderEmailLabel"} initialValue={"alias"}>
          <Radio.Group className="w-100" onChange={triggerFormChanged}>
            <Row gutter={32}>
              <Col span={24} md={8} className="d-flex align-items-center">
                <Radio
                  value="alias"
                  data-test={"input-label"}
                  data-test-for={`${mailType}-senderEmailLabel-alias-text`}
                  data-alt={`${t(
                    "offices.mailModel.form.senderEmailLabel.alias",
                  )} - ${mailType}`}
                >
                  {t("offices.mailModel.form.senderEmailLabel.alias")}
                </Radio>
              </Col>
              <Col span={24} md={8} className="d-flex align-items-center">
                <Radio
                  value="jesignexpert"
                  data-test={"input-label"}
                  data-test-for={`${mailType}-senderEmailLabel-jesignexpert-text`}
                  data-alt={`${t(
                    "offices.mailModel.form.senderEmailLabel.jesignexpert",
                  )} - ${mailType}`}
                >
                  {t("offices.mailModel.form.senderEmailLabel.jesignexpert")}
                </Radio>
              </Col>
              <Col span={24} md={8} className="d-flex align-items-center">
                <Radio
                  value="custom"
                  data-test={"input-label"}
                  data-alt={`${t("offices.mailModel.form.senderEmailLabel.custom")} - ${mailType}`}
                ></Radio>
                <div
                  data-test={"input-label"}
                  data-test-for={`${mailType}-senderEmailLabel-custom-text`}
                  data-alt={`${t("offices.mailModel.form.senderEmailLabel.custom")} (texte)`}
                >
                  <InputFormField
                    module="offices.mailModel.form"
                    field={"senderEmailLabelText"}
                    placeholder={t(
                      "offices.mailModel.form.senderEmailLabel.custom",
                    )}
                    maxLength={100}
                    className="mb-0"
                    dataTest={`${mailType}-senderEmailLabel-custom-text`}
                    onChange={triggerFormChanged}
                  />
                </div>
              </Col>
            </Row>
          </Radio.Group>
        </Form.Item>
        <InputFormField
          showLabel
          module={`offices.mailModel.form.${mailType}`}
          field={"subject"}
          maxLength={78}
          className="mt-16 mb-24"
          onChange={triggerFormChanged}
        />
        <TextAreaFormField
          showLabel
          module={`offices.mailModel.form.${mailType}`}
          field={"message"}
          maxLength={1000}
          className="mb-24"
          onChange={triggerFormChanged}
        />
        <Form.Item name={"signature"}>
          <RemirrorField
            key={signatureInitialValue} // Force component reload on initialValue change
            label={t(`offices.mailModel.form.${mailType}.signature.label`)}
            placeholder={t(
              `offices.mailModel.form.${mailType}.signature.placeholder`,
            )}
            initialValue={signatureInitialValue}
            setSignature={(value) => form?.setFieldValue("signature", value)}
            onUserAction={triggerFormChanged}
          />
        </Form.Item>
      </div>
      {isFromModel && (
        <>
          <div className="d-flex justify-content-center mt-16">
            <BasicButton
              variant="transparent"
              icon={<ViewIcon />}
              text={t(`offices.mailModel.form.${mailType}.preview.label`)}
              onClick={() => setIsPreviewMailModalOpen(true)}
            />
          </div>
          <div className="mt-30 d-flex align-items-center justify-content-end flex-wrap gap-16">
            <div className="flex-grow-1">
              <CheckboxFormField
                field={"defaultModel"}
                module={""}
                label={t(
                  "alertPreferences.emailPersonnalisation.useDefault.title",
                )}
              />
            </div>
            <SubmitButton label={t("buttons.save")} />
          </div>
        </>
      )}
      <PreviewMailModal
        visible={isPreviewMailModalOpen}
        onCancel={() => setIsPreviewMailModalOpen(false)}
        sender={correctSender()}
        subject={correctSubject()}
        mailType={mailType}
        message={mailConfiguration.message}
        signature={mailConfiguration.signature}
      />
      {!isFromModel && (
        <ChangeModelMailModal
          visible={isChangeModelMailModalOpen}
          onCancel={() => setIsChangeModelMailModalOpen(false)}
          setIsMailContentVisible={setIsMailContentVisible}
          modelsList={modelsList}
          onSubmit={handleChangeModelMail}
        />
      )}
    </>
  );
};

export default SignatureMailForm;
